"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { coordKey, getDirection } from "../../hex-path/hex-grid";
import type { HexCoord } from "../../hex-path/types";
import type {
	ThumbMovementDriveState,
	ThumbMovementVisualState,
} from "../components/ThumbMovementControls";
import {
	type CharacterMovementDisplaySnapshot,
	resolveCharacterRenderCenter,
} from "../hooks/use-hexagon-map-character-movement";
import { useHexagonMapThumbKeyboard } from "../hooks/use-hexagon-map-thumb-keyboard";
import { facingRadFromScreenDelta } from "../lib/hex-character-facing";
import type { HexagonLayoutMetrics } from "../lib/hexagon-layout-metrics";
import {
	isPathRouteTraversalEnabled,
	isPathTileTraversalEnabled,
	resolveNearestPathTileFromDisplayCenter,
	resolvePseudoTargetForHexDirection,
} from "../lib/path-character-movement";
import {
	resolveHexDirectionFromThumbDirection,
	resolveHexMapAnalogSpeedScale,
	resolvePathNeighborForHexDirection,
	resolvePeakHeldMagnitude,
	resolveStickyPathNeighborFromScreenVector,
	resolveThumbDirectionFromHexDirection,
	resolveThumbOffsetFromDriveState,
	resolveThumbStepDurationMs,
	THUMB_ANALOG_INNER_DEADZONE,
	THUMB_CONTROL_MAX_DRAG_PX,
	THUMB_HEX_STEP_COMMIT_DEADZONE,
} from "../lib/thumb-movement-direction";

interface CharacterMovementController {
	isMoving: boolean;
	resolvePathOriginCoord: () => HexCoord | null;
	startMovement: (
		route: HexCoord[],
		options?: {
			stepDurationMs?: number;
			speedScale?: number;
			visualFromCenter?: { left: number; top: number };
		},
	) => boolean;
	setFacingForStep: (from: HexCoord, to: HexCoord) => void;
	setFacingRad: (facingRad: number) => void;
	snapToCoord: (coord: HexCoord) => void;
	cancelMovement: () => void;
	cancelMovementKeepingVisual: (logicalCoord?: HexCoord | null) => void;
	commitLogicalCoord: (coord: HexCoord) => void;
	retargetActiveStepLocomotion: (input: {
		speedScale: number;
		stepDurationMs: number;
	}) => void;
	getDisplaySnapshot: () => CharacterMovementDisplaySnapshot;
	subscribeDisplay: (listener: () => void) => () => void;
}

export interface UseHexagonMapThumbMovementOptions {
	characterMovement: CharacterMovementController;
	characterMovementMsPerTile: number;
	commitPlayerPathPositionForCoord: (coord: HexCoord) => void;
	disabled?: boolean;
	furthestUnlockedPathProgress: number;
	isPathIndexVisible: (pathIndex: number) => boolean;
	keyboardEnabled?: boolean;
	metrics: HexagonLayoutMetrics;
	onMapPathSelectionChange?: (stopId: string | null) => void;
	pathCellByCoord: Map<string, { nodeId?: string | null }>;
	pathCoordSet: Set<string>;
	pathIndexByCoordKey: Map<string, number>;
	pathProgressByCoordKey: Map<string, number>;
	restingDisplayCenter: { left: number; top: number } | null;
}

function resolveStopIdAtCoord(
	coord: HexCoord,
	pathCellByCoord: Map<string, { nodeId?: string | null }>,
): string | null {
	const pathCell = pathCellByCoord.get(coordKey(coord.row, coord.col));
	if (pathCell?.nodeId?.startsWith("sq:map:stop:") !== true) {
		return null;
	}
	return pathCell.nodeId.replace("sq:map:stop:", "");
}

function resolveSyncedVisualFromSnapshot(
	snapshot: CharacterMovementDisplaySnapshot,
): ThumbMovementVisualState | null {
	if (
		!snapshot.isMoving ||
		snapshot.activeStepFrom == null ||
		snapshot.activeStepTo == null
	) {
		return null;
	}

	const hexDirection = getDirection(
		snapshot.activeStepFrom.row,
		snapshot.activeStepFrom.col,
		snapshot.activeStepTo.row,
		snapshot.activeStepTo.col,
	);
	const direction = resolveThumbDirectionFromHexDirection(hexDirection);

	return {
		activeDirection: direction,
		thumbOffset: resolveThumbOffsetFromDriveState(
			direction,
			1,
			THUMB_CONTROL_MAX_DRAG_PX,
		),
	};
}

function thumbVisualEquals(
	left: ThumbMovementVisualState | null,
	right: ThumbMovementVisualState | null,
): boolean {
	if (left === right) {
		return true;
	}
	if (left == null || right == null) {
		return false;
	}
	return (
		left.activeDirection === right.activeDirection &&
		left.thumbOffset.dx === right.thumbOffset.dx &&
		left.thumbOffset.dy === right.thumbOffset.dy
	);
}

function scheduleMicrotask(task: () => void): void {
	if (typeof queueMicrotask === "function") {
		queueMicrotask(task);
		return;
	}
	Promise.resolve().then(task);
}

export function useHexagonMapThumbMovement(
	options: UseHexagonMapThumbMovementOptions,
) {
	const {
		characterMovement,
		characterMovementMsPerTile,
		commitPlayerPathPositionForCoord,
		disabled = false,
		furthestUnlockedPathProgress,
		isPathIndexVisible,
		keyboardEnabled = false,
		metrics,
		onMapPathSelectionChange,
		pathCellByCoord,
		pathCoordSet,
		pathIndexByCoordKey,
		pathProgressByCoordKey,
		restingDisplayCenter,
	} = options;

	const characterMovementRef = useRef(characterMovement);
	characterMovementRef.current = characterMovement;

	const commitPlayerPathPositionForCoordRef = useRef(
		commitPlayerPathPositionForCoord,
	);
	commitPlayerPathPositionForCoordRef.current =
		commitPlayerPathPositionForCoord;

	const onMapPathSelectionChangeRef = useRef(onMapPathSelectionChange);
	onMapPathSelectionChangeRef.current = onMapPathSelectionChange;

	const restingDisplayCenterRef = useRef(restingDisplayCenter);
	restingDisplayCenterRef.current = restingDisplayCenter;

	const driveStateRef = useRef<ThumbMovementDriveState | null>(null);
	const isPointerDrivingRef = useRef(false);
	const isKeyboardDrivingRef = useRef(false);
	const isDrivingRef = useRef(false);
	const releaseFinalizePendingRef = useRef(false);
	const wasCharacterMovingRef = useRef(false);
	const lastSyncedStepKeyRef = useRef<string | null>(null);
	const suppressNextMovementCompleteChainRef = useRef(false);
	const finalizeTaskScheduledRef = useRef(false);
	const thumbSyncedVisualRef = useRef<ThumbMovementVisualState | null>(null);
	const [thumbSyncedVisual, setThumbSyncedVisual] =
		useState<ThumbMovementVisualState | null>(null);

	const setThumbSyncedVisualIfChanged = useCallback(
		(next: ThumbMovementVisualState | null) => {
			if (thumbVisualEquals(thumbSyncedVisualRef.current, next)) {
				return;
			}
			thumbSyncedVisualRef.current = next;
			setThumbSyncedVisual(next);
		},
		[],
	);

	const refreshDrivingState = useCallback(() => {
		isDrivingRef.current =
			isPointerDrivingRef.current || isKeyboardDrivingRef.current;
	}, []);

	const refreshThumbSyncedVisual = useCallback(() => {
		if (isPointerDrivingRef.current) {
			setThumbSyncedVisualIfChanged(null);
			return;
		}

		if (isKeyboardDrivingRef.current && driveStateRef.current) {
			const { direction, magnitude } = driveStateRef.current;
			setThumbSyncedVisualIfChanged({
				activeDirection: direction,
				thumbOffset: resolveThumbOffsetFromDriveState(
					direction,
					magnitude,
					THUMB_CONTROL_MAX_DRAG_PX,
				),
			});
			return;
		}

		const syncedVisual = resolveSyncedVisualFromSnapshot(
			characterMovementRef.current.getDisplaySnapshot(),
		);
		setThumbSyncedVisualIfChanged(syncedVisual);
	}, [setThumbSyncedVisualIfChanged]);

	const stickyIntentNeighborRef = useRef<HexCoord | null>(null);
	const queuedNextNeighborRef = useRef<HexCoord | null>(null);
	/**
	 * Prevents complete-chain + drive-sample from both calling startMovement
	 * in the same idle gap (that restarted hops and looked like teleporting).
	 */
	const stepStartLockRef = useRef(false);
	/** Peak stick magnitude while driving — used for hop speed bake/retarget. */
	const peakMagnitudeRef = useRef(0);

	const resolveThumbOriginCoord = useCallback((): HexCoord | null => {
		return characterMovementRef.current.resolvePathOriginCoord();
	}, []);

	const isNeighborTraversable = useCallback(
		(from: HexCoord, neighbor: HexCoord): boolean => {
			const neighborKey = coordKey(neighbor.row, neighbor.col);
			const neighborPathProgress =
				pathProgressByCoordKey.get(neighborKey);
			if (neighborPathProgress == null) {
				return false;
			}
			if (
				!isPathTileTraversalEnabled({
					row: neighbor.row,
					col: neighbor.col,
					pathProgress: neighborPathProgress,
					pathCoordSet,
					furthestUnlockedPathProgress,
					isPathIndexVisible,
					pathIndexForVisibility:
						pathIndexByCoordKey.get(neighborKey),
				})
			) {
				return false;
			}
			return isPathRouteTraversalEnabled({
				route: [from, neighbor],
				pathCoordSet,
				pathProgressByCoordKey,
				pathIndexByCoordKey,
				furthestUnlockedPathProgress,
				isPathIndexVisible,
			});
		},
		[
			furthestUnlockedPathProgress,
			isPathIndexVisible,
			pathCoordSet,
			pathIndexByCoordKey,
			pathProgressByCoordKey,
		],
	);

	const resolveLocomotionNeighbor = useCallback(
		(
			originCoord: HexCoord,
			driveState: ThumbMovementDriveState,
			excludeNeighbor?: HexCoord | null,
		) => {
			const allow = (neighbor: HexCoord) => {
				if (
					excludeNeighbor &&
					neighbor.row === excludeNeighbor.row &&
					neighbor.col === excludeNeighbor.col
				) {
					return false;
				}
				return isNeighborTraversable(originCoord, neighbor);
			};
			const vector = driveState.vector;
			if (vector != null && Math.hypot(vector.x, vector.y) > 1e-6) {
				const sticky = resolveStickyPathNeighborFromScreenVector(
					originCoord,
					vector,
					pathCoordSet,
					stickyIntentNeighborRef.current,
					allow,
				);
				stickyIntentNeighborRef.current = sticky;
				return sticky;
			}
			/** Keyboard / chrome-only: fall back to sector → hex edge. */
			const hexDirection = resolveHexDirectionFromThumbDirection(
				driveState.direction,
				{ origin: originCoord, pathCoordSet },
			);
			const neighbor = resolvePathNeighborForHexDirection(
				originCoord,
				hexDirection,
				pathCoordSet,
			);
			if (neighbor && allow(neighbor)) {
				stickyIntentNeighborRef.current = neighbor;
				return neighbor;
			}
			stickyIntentNeighborRef.current = null;
			return null;
		},
		[isNeighborTraversable, pathCoordSet],
	);

	const applyThumbFacing = useCallback(
		(originCoord: HexCoord, driveState: ThumbMovementDriveState) => {
			const vector = driveState.vector;
			const fromStick =
				vector != null
					? facingRadFromScreenDelta(vector.x, vector.y)
					: null;
			if (fromStick != null) {
				characterMovementRef.current.setFacingRad(fromStick);
				return;
			}
			const hexDirection = resolveHexDirectionFromThumbDirection(
				driveState.direction,
				{ origin: originCoord, pathCoordSet },
			);
			const facingTarget =
				resolvePathNeighborForHexDirection(
					originCoord,
					hexDirection,
					pathCoordSet,
				) ??
				resolvePseudoTargetForHexDirection(originCoord, hexDirection);
			characterMovementRef.current.setFacingForStep(
				originCoord,
				facingTarget,
			);
		},
		[pathCoordSet],
	);

	const resolveDriveSpeed = useCallback(
		(magnitude: number) => {
			const peak = resolvePeakHeldMagnitude(
				magnitude,
				peakMagnitudeRef.current,
			);
			peakMagnitudeRef.current = peak;
			const speedScale = resolveHexMapAnalogSpeedScale(peak);
			const stepDurationMs = resolveThumbStepDurationMs(
				peak,
				characterMovementMsPerTile,
			);
			return { speedScale, stepDurationMs, peak };
		},
		[characterMovementMsPerTile],
	);

	const startThumbStepToNeighbor = useCallback(
		(
			pathOrigin: HexCoord,
			neighbor: HexCoord,
			driveState: ThumbMovementDriveState,
			visualFromCenter?: { left: number; top: number },
		): boolean => {
			const movement = characterMovementRef.current;
			if (movement.getDisplaySnapshot().isMoving) {
				return false;
			}
			if (stepStartLockRef.current) {
				return false;
			}
			if (!isNeighborTraversable(pathOrigin, neighbor)) {
				return false;
			}
			stepStartLockRef.current = true;
			const { speedScale, stepDurationMs } = resolveDriveSpeed(
				driveState.magnitude,
			);
			const started = movement.startMovement([pathOrigin, neighbor], {
				stepDurationMs,
				speedScale,
				visualFromCenter,
			});
			if (!started || !movement.getDisplaySnapshot().isMoving) {
				stepStartLockRef.current = false;
				return false;
			}
			return true;
		},
		[isNeighborTraversable, resolveDriveSpeed],
	);

	const tryThumbStep = useCallback(() => {
		if (disabled || !isDrivingRef.current) {
			return;
		}
		const driveState = driveStateRef.current;
		if (!driveState || driveState.magnitude < THUMB_ANALOG_INNER_DEADZONE) {
			return;
		}

		const originCoord = resolveThumbOriginCoord();
		if (!originCoord) {
			return;
		}

		const movement = characterMovementRef.current;
		const snapshot = movement.getDisplaySnapshot();
		/** Continuous facing — independent of step commitment. */
		applyThumbFacing(originCoord, driveState);

		/** Live speed retarget — never stay stuck on the ramp-up sneak bake. */
		if (snapshot.isMoving) {
			const { speedScale, stepDurationMs } = resolveDriveSpeed(
				driveState.magnitude,
			);
			movement.retargetActiveStepLocomotion({
				speedScale,
				stepDurationMs,
			});
		}

		/**
		 * While hopping, score next edge from the destination and never queue
		 * an immediate reverse onto activeStepFrom (that caused opposite-dir
		 * / snap-back flickers).
		 */
		const excludeReverse = snapshot.isMoving
			? snapshot.activeStepFrom
			: null;
		const desiredNeighbor = resolveLocomotionNeighbor(
			originCoord,
			driveState,
			excludeReverse,
		);

		if (snapshot.isMoving) {
			stepStartLockRef.current = false;
			const stepTo = snapshot.activeStepTo;
			if (
				stepTo &&
				desiredNeighbor &&
				stepTo.row === desiredNeighbor.row &&
				stepTo.col === desiredNeighbor.col
			) {
				queuedNextNeighborRef.current = null;
				return;
			}
			if (
				desiredNeighbor &&
				stepTo &&
				(desiredNeighbor.row !== stepTo.row ||
					desiredNeighbor.col !== stepTo.col)
			) {
				queuedNextNeighborRef.current = desiredNeighbor;
			}
			return;
		}

		if (!desiredNeighbor) {
			return;
		}
		/** Wait for commit threshold before baking the first/next idle hop. */
		if (driveState.magnitude < THUMB_HEX_STEP_COMMIT_DEADZONE) {
			return;
		}

		suppressNextMovementCompleteChainRef.current = false;
		queuedNextNeighborRef.current = null;
		startThumbStepToNeighbor(originCoord, desiredNeighbor, driveState);
	}, [
		applyThumbFacing,
		disabled,
		resolveDriveSpeed,
		resolveLocomotionNeighbor,
		resolveThumbOriginCoord,
		startThumbStepToNeighbor,
	]);

	const tryThumbStepRef = useRef(tryThumbStep);
	tryThumbStepRef.current = tryThumbStep;

	const refreshThumbSyncedVisualRef = useRef(refreshThumbSyncedVisual);
	refreshThumbSyncedVisualRef.current = refreshThumbSyncedVisual;

	const finalizeThumbRelease = useCallback(() => {
		releaseFinalizePendingRef.current = false;
		finalizeTaskScheduledRef.current = false;

		if (isDrivingRef.current) {
			return;
		}

		const movement = characterMovementRef.current;
		const display = movement.getDisplaySnapshot();
		const renderCenter = resolveCharacterRenderCenter(
			display,
			restingDisplayCenterRef.current,
		);
		if (!renderCenter) {
			return;
		}

		const nearestCoord = resolveNearestPathTileFromDisplayCenter(
			renderCenter,
			pathCoordSet,
			metrics,
		);
		if (!nearestCoord) {
			return;
		}

		movement.snapToCoord(nearestCoord);
		// Sole server sync point for thumb/keyboard drive: local steps stay client-only.
		commitPlayerPathPositionForCoordRef.current(nearestCoord);

		const stopId = resolveStopIdAtCoord(nearestCoord, pathCellByCoord);
		if (stopId) {
			onMapPathSelectionChangeRef.current?.(stopId);
			return;
		}
		onMapPathSelectionChangeRef.current?.(null);
	}, [metrics, pathCellByCoord, pathCoordSet]);

	const scheduleFinalizeThumbRelease = useCallback(() => {
		releaseFinalizePendingRef.current = true;
		if (finalizeTaskScheduledRef.current) {
			return;
		}

		const runFinalize = () => {
			if (!releaseFinalizePendingRef.current) {
				finalizeTaskScheduledRef.current = false;
				return;
			}
			const movement = characterMovementRef.current;
			if (movement.getDisplaySnapshot().isMoving) {
				const unsubscribe = movement.subscribeDisplay(() => {
					if (movement.getDisplaySnapshot().isMoving) {
						return;
					}
					unsubscribe();
					scheduleMicrotask(() => {
						if (releaseFinalizePendingRef.current) {
							finalizeThumbRelease();
						}
					});
				});
				return;
			}
			finalizeThumbRelease();
		};

		finalizeTaskScheduledRef.current = true;
		scheduleMicrotask(runFinalize);
	}, [finalizeThumbRelease]);

	const handleThumbDriveChange = useCallback(
		(state: ThumbMovementDriveState | null) => {
			if (disabled) {
				return;
			}
			releaseFinalizePendingRef.current = false;
			finalizeTaskScheduledRef.current = false;
			driveStateRef.current = state;
			if (!state) {
				peakMagnitudeRef.current = 0;
				refreshThumbSyncedVisualRef.current();
				return;
			}
			peakMagnitudeRef.current = resolvePeakHeldMagnitude(
				state.magnitude,
				peakMagnitudeRef.current,
			);
			refreshThumbSyncedVisualRef.current();
			tryThumbStepRef.current();
		},
		[disabled],
	);

	const endUserDriving = useCallback(() => {
		if (isDrivingRef.current) {
			return;
		}
		driveStateRef.current = null;
		peakMagnitudeRef.current = 0;
		stickyIntentNeighborRef.current = null;
		queuedNextNeighborRef.current = null;
		scheduleFinalizeThumbRelease();
		refreshThumbSyncedVisualRef.current();
	}, [scheduleFinalizeThumbRelease]);

	const handlePointerInteractionChange = useCallback(
		(isActive: boolean) => {
			isPointerDrivingRef.current = isActive;
			refreshDrivingState();
			if (isActive) {
				releaseFinalizePendingRef.current = false;
				finalizeTaskScheduledRef.current = false;
				/** Do not interrupt in-flight hops — step commitment. */
			}
			refreshThumbSyncedVisualRef.current();
		},
		[refreshDrivingState],
	);

	const handlePointerDriveEnd = useCallback(() => {
		isPointerDrivingRef.current = false;
		refreshDrivingState();
		endUserDriving();
	}, [endUserDriving, refreshDrivingState]);

	const handleKeyboardDrivingChange = useCallback(
		(isActive: boolean) => {
			isKeyboardDrivingRef.current = isActive;
			refreshDrivingState();
			if (isActive) {
				releaseFinalizePendingRef.current = false;
				finalizeTaskScheduledRef.current = false;
			}
			refreshThumbSyncedVisualRef.current();
		},
		[refreshDrivingState],
	);

	const handleKeyboardDriveEnd = useCallback(() => {
		isKeyboardDrivingRef.current = false;
		refreshDrivingState();
		endUserDriving();
	}, [endUserDriving, refreshDrivingState]);

	useHexagonMapThumbKeyboard({
		enabled: keyboardEnabled && !disabled,
		onDriveChange: handleThumbDriveChange,
		onDriveEnd: handleKeyboardDriveEnd,
		onKeyboardDrivingChange: handleKeyboardDrivingChange,
	});

	const subscribeDisplay = characterMovement.subscribeDisplay;

	useEffect(() => {
		if (disabled) {
			isPointerDrivingRef.current = false;
			isKeyboardDrivingRef.current = false;
			isDrivingRef.current = false;
			driveStateRef.current = null;
			wasCharacterMovingRef.current = false;
			lastSyncedStepKeyRef.current = null;
			stickyIntentNeighborRef.current = null;
			queuedNextNeighborRef.current = null;
			stepStartLockRef.current = false;
			peakMagnitudeRef.current = 0;
			releaseFinalizePendingRef.current = false;
			finalizeTaskScheduledRef.current = false;
			setThumbSyncedVisualIfChanged(null);
			return;
		}

		const initial = characterMovementRef.current.getDisplaySnapshot();
		wasCharacterMovingRef.current = initial.isMoving;
		lastSyncedStepKeyRef.current =
			initial.activeStepFrom && initial.activeStepTo
				? `${initial.activeStepFrom.row},${initial.activeStepFrom.col}->${initial.activeStepTo.row},${initial.activeStepTo.col}`
				: null;
		refreshThumbSyncedVisualRef.current();

		return subscribeDisplay(() => {
			const movement = characterMovementRef.current;
			const snapshot = movement.getDisplaySnapshot();
			const wasMoving = wasCharacterMovingRef.current;
			const movingChanged = wasMoving !== snapshot.isMoving;
			wasCharacterMovingRef.current = snapshot.isMoving;
			const stepKey =
				snapshot.activeStepFrom && snapshot.activeStepTo
					? `${snapshot.activeStepFrom.row},${snapshot.activeStepFrom.col}->${snapshot.activeStepTo.row},${snapshot.activeStepTo.col}`
					: null;
			const stepChanged = stepKey !== lastSyncedStepKeyRef.current;
			if (stepChanged) {
				lastSyncedStepKeyRef.current = stepKey;
			}

			/** Avoid React thumb UI updates on every mover frame. */
			if (
				movingChanged ||
				stepChanged ||
				isKeyboardDrivingRef.current ||
				isPointerDrivingRef.current
			) {
				refreshThumbSyncedVisualRef.current();
			}

			if (snapshot.isMoving) {
				stepStartLockRef.current = false;
				return;
			}
			if (!isDrivingRef.current || !wasMoving) {
				return;
			}
			if (suppressNextMovementCompleteChainRef.current) {
				suppressNextMovementCompleteChainRef.current = false;
				queuedNextNeighborRef.current = null;
				return;
			}

			/**
			 * Sole owner of post-hop chaining — drive samples must not also
			 * start a step in this idle gap (see stepStartLockRef).
			 */
			stepStartLockRef.current = false;
			const driveState = driveStateRef.current;
			if (
				!driveState ||
				driveState.magnitude < THUMB_ANALOG_INNER_DEADZONE
			) {
				queuedNextNeighborRef.current = null;
				return;
			}
			const origin =
				characterMovementRef.current.resolvePathOriginCoord();
			if (!origin) {
				queuedNextNeighborRef.current = null;
				return;
			}
			if (driveState.magnitude < THUMB_HEX_STEP_COMMIT_DEADZONE) {
				queuedNextNeighborRef.current = null;
				return;
			}
			const queued = queuedNextNeighborRef.current;
			queuedNextNeighborRef.current = null;
			if (queued && isNeighborTraversable(origin, queued)) {
				startThumbStepToNeighbor(origin, queued, driveState);
				return;
			}
			const next = resolveLocomotionNeighbor(origin, driveState, null);
			if (next) {
				startThumbStepToNeighbor(origin, next, driveState);
			}
		});
	}, [
		disabled,
		isNeighborTraversable,
		resolveLocomotionNeighbor,
		setThumbSyncedVisualIfChanged,
		startThumbStepToNeighbor,
		subscribeDisplay,
	]);

	return {
		handlePointerDriveEnd,
		handlePointerInteractionChange,
		handleThumbDriveChange,
		thumbSyncedVisual,
	};
}
